import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getMinistry, createReimbursement, getReimbursementForClone } from '../services/api';
import { formatCpfCnpj, validateCpfCnpj } from '../utils/formatters';
import ProgressBar from '../components/ProgressBar';
import './RequestReimbursement.css';

const RequestReimbursement = () => {
  const { ministryId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cloneFromId = searchParams.get('cloneFrom');
  const [ministry, setMinistry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCloning, setIsCloning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [cpfCnpjError, setCpfCnpjError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [formData, setFormData] = useState({
    requesterName: '',
    requesterCpfCnpj: '',
    pixKey: '',
    amount: '',
    description: ''
  });
  const [file, setFile] = useState(null);
  const [isAdvancePayment, setIsAdvancePayment] = useState(false);

  useEffect(() => {
    loadData();
  }, [ministryId, cloneFromId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load ministry data
      const ministryResponse = await getMinistry(ministryId, new Date().getFullYear());
      setMinistry(ministryResponse.data);
      
      // If cloning, load the original reimbursement data
      if (cloneFromId) {
        setIsCloning(true);
        try {
          const cloneResponse = await getReimbursementForClone(cloneFromId);
          const cloneData = cloneResponse.data;
          
          // Format amount for display
          const formattedAmount = cloneData.amount.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
          
          setFormData({
            requesterName: cloneData.requesterName,
            requesterCpfCnpj: cloneData.requesterCpfCnpj,
            pixKey: cloneData.pixKey,
            amount: formattedAmount,
            description: cloneData.description
          });
        } catch (cloneError) {
          console.error('Erro ao carregar dados para clonar:', cloneError);
          setError('Erro ao carregar dados do reembolso original. Usando dados padrão.');
          setIsCloning(false);
          
          // Fallback to leader's data
          setFormData({
            requesterName: ministryResponse.data.leader.name,
            requesterCpfCnpj: ministryResponse.data.leader.cpfCnpj,
            pixKey: '',
            amount: '',
            description: ''
          });
        }
      } else {
        // Pre-fill with leader's data
        setFormData({
          requesterName: ministryResponse.data.leader.name,
          requesterCpfCnpj: ministryResponse.data.leader.cpfCnpj,
          pixKey: '',
          amount: '',
          description: ''
        });
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setError('Erro ao carregar informações do ministério');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate CPF/CNPJ before submission
    if (!validateCpfCnpj(formData.requesterCpfCnpj)) {
      setCpfCnpjError('CPF/CNPJ inválido');
      setError('Por favor, corrija os erros no formulário');
      return;
    }
    
    // File is required only if not advance payment
    if (!isAdvancePayment && !file) {
      setError('Por favor, anexe a nota fiscal');
      return;
    }

    setSubmitting(true);
    setError('');
    setUploadProgress(0);
    setUploadStatus('Preparando envio...');

    try {
      const data = new FormData();
      data.append('ministryId', ministryId);
      data.append('requesterName', formData.requesterName);
      data.append('requesterCpfCnpj', formData.requesterCpfCnpj);
      data.append('pixKey', formData.pixKey);
      data.append('amount', parseAmount(formData.amount));
      data.append('description', formData.description);
      data.append('isAdvancePayment', isAdvancePayment);
      if (file) {
        data.append('file', file);
      }

      setUploadStatus('Enviando arquivo...');

      await createReimbursement(data, (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percentCompleted);
        
        if (percentCompleted < 100) {
          setUploadStatus('Enviando arquivo...');
        } else {
          setUploadStatus('Processando...');
        }
      });
      
      setUploadStatus('Concluído!');
      setUploadProgress(100);
      
      setTimeout(() => {
        alert('Reembolso solicitado com sucesso!');
        navigate(`/ministry/${ministryId}`);
      }, 500);
    } catch (error) {
      console.error('Erro ao solicitar reembolso:', error);
      setError(error.response?.data?.message || 'Erro ao solicitar reembolso. Tente novamente.');
      setUploadProgress(0);
      setUploadStatus('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file size (max 10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('Arquivo muito grande. Tamanho máximo: 10MB');
        e.target.value = '';
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleCpfCnpjChange = (e) => {
    const value = e.target.value;
    const formatted = formatCpfCnpj(value);
    setFormData({ ...formData, requesterCpfCnpj: formatted });
    
    // Clear error when user starts typing
    if (cpfCnpjError) {
      setCpfCnpjError('');
    }
  };

  const handleCpfCnpjBlur = () => {
    const cleaned = formData.requesterCpfCnpj.replace(/\D/g, '');
    
    // Only validate if there's content
    if (cleaned.length > 0) {
      if (cleaned.length !== 11 && cleaned.length !== 14) {
        setCpfCnpjError('CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos');
      } else if (!validateCpfCnpj(formData.requesterCpfCnpj)) {
        setCpfCnpjError('CPF/CNPJ inválido');
      } else {
        setCpfCnpjError('');
      }
    }
  };

  const formatCurrencyInput = (value) => {
    // Remove tudo exceto números
    const numbers = value.replace(/\D/g, '');
    
    if (!numbers) return '';
    
    // Converte para número e divide por 100 para ter os centavos
    const amount = parseFloat(numbers) / 100;
    
    // Formata como moeda brasileira
    return amount.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    const formatted = formatCurrencyInput(value);
    setFormData({ ...formData, amount: formatted });
  };

  const parseAmount = (formattedValue) => {
    // Converte o valor formatado de volta para número
    const numbers = formattedValue.replace(/\D/g, '');
    return parseFloat(numbers) / 100;
  };

  if (loading) {
    return <div className="loading">Carregando...</div>;
  }

  if (!ministry) {
    return (
      <div className="container">
        <div className="card">Ministério não encontrado</div>
      </div>
    );
  }

  return (
    <div className="request-reimbursement-page">
      <div className="container">
        <div className="page-header">
          <h1>Solicitar Reembolso</h1>
          <p>Ministério: {ministry.name}</p>
        </div>

        {isCloning && (
          <div className="alert alert-info" style={{ marginBottom: '20px' }}>
            <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
            <strong>Clonando pedido:</strong> Os dados foram pré-preenchidos a partir de um pedido anterior. 
            Você pode modificá-los conforme necessário. A nota fiscal não foi copiada e deve ser anexada novamente.
          </div>
        )}

        <div className="card">
          <form onSubmit={handleSubmit} className="reimbursement-form">
            {error && (
              <div className="error-message">
                <span>{error}</span>
                {error.includes('Tente novamente') && (
                  <button 
                    type="button" 
                    className="btn btn-sm btn-secondary"
                    onClick={() => setError('')}
                    style={{ marginLeft: '10px' }}
                  >
                    Tentar Novamente
                  </button>
                )}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">
                Nome do Solicitante <span className="required">*</span>
              </label>
              <input
                type="text"
                className="form-input"
                value={formData.requesterName}
                onChange={(e) => setFormData({ ...formData, requesterName: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                CPF/CNPJ <span className="required">*</span>
              </label>
              <input
                type="text"
                className={`form-input ${cpfCnpjError ? 'error' : ''}`}
                value={formData.requesterCpfCnpj}
                onChange={handleCpfCnpjChange}
                onBlur={handleCpfCnpjBlur}
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                maxLength="18"
                required
              />
              {cpfCnpjError && (
                <small className="error-text">{cpfCnpjError}</small>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                Chave PIX <span className="required">*</span>
              </label>
              <input
                type="text"
                className="form-input"
                value={formData.pixKey}
                onChange={(e) => setFormData({ ...formData, pixKey: e.target.value })}
                placeholder="CPF, CNPJ, E-mail, Telefone ou Chave Aleatória"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Valor (R$) <span className="required">*</span>
              </label>
              <input
                type="text"
                className="form-input"
                value={formData.amount}
                onChange={handleAmountChange}
                placeholder="0,00"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Valor Referente a <span className="required">*</span>
              </label>
              <textarea
                className="form-textarea"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descreva detalhadamente o motivo do reembolso, o que foi adquirido, para qual finalidade, etc."
                required
                rows="5"
              />
            </div>

            <div className="form-group">
              <div className="advance-payment-toggle">
                <label className="toggle-container">
                  <input
                    type="checkbox"
                    checked={isAdvancePayment}
                    onChange={(e) => setIsAdvancePayment(e.target.checked)}
                    disabled={submitting}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-label">
                    <i className="fas fa-hand-holding-usd"></i>
                    Solicitar Adiantamento
                  </span>
                </label>
                {isAdvancePayment && (
                  <div className="advance-info">
                    <i className="fas fa-info-circle"></i>
                    <span>Você poderá anexar a nota fiscal posteriormente após receber o pagamento.</span>
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Nota Fiscal {!isAdvancePayment && <span className="required">*</span>}
                {isAdvancePayment && <span className="optional-badge">Opcional</span>}
              </label>
              <input
                type="file"
                className="form-input"
                onChange={handleFileChange}
                accept="image/*,.pdf,.doc,.docx"
                required={!isAdvancePayment}
                disabled={submitting}
              />
              <small className="form-help">
                Formatos aceitos: PDF, Imagens, DOC, DOCX (máx. 10MB)
                {isAdvancePayment && <span className="text-info"> - Pode ser anexada posteriormente</span>}
              </small>
              {file && !submitting && (
                <div className="file-selected">
                  <i className="fas fa-check-circle"></i> Arquivo selecionado: {file.name}
                </div>
              )}
            </div>

            {submitting && uploadStatus && (
              <ProgressBar 
                progress={uploadProgress} 
                status={uploadStatus}
                fileName={file?.name}
              />
            )}

            <div className="form-actions">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Enviando...' : 'Enviar Solicitação'}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => navigate(`/ministry/${ministryId}`)}
                disabled={submitting}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RequestReimbursement;
